import { AxiosError } from 'axios'
import ToolsGrid from '../../components/grids/ToolsGrid'
import AddToolForm from '../../components/FormComps/Tools/AddToolForm'
import EditToolForm from '../../components/FormComps/Tools/EditToolForm'

import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../contexts/ToastContext'

import {
  Drawer,
  DrawerContent,
  DrawerNavigation,
} from '@progress/kendo-react-layout'
import { process } from '@progress/kendo-data-query'
import { FaTimesCircle } from 'react-icons/fa'
import {
  getTools,
  submitTool,
  updateTool,
  deleteTool,
} from '../../data/toolData'
import { Tool, PostTool, UpdateTool } from '../../data/interfaces'
import { canEditArea } from '../../helpers/areaAccess'

import '../Styles.css'

interface ToolsProps {
  allowedRoutes?: string[];
}

const Tools = (props: ToolsProps) => {
  const navigate = useNavigate()
  // Tools.View only looks; Tools.Edit or an administrator change. The server enforces the same (Manage.Tools.*).
  const canEdit = canEditArea(props.allowedRoutes, 'Tools')

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [toolData, setToolData] = useState<Tool[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [editFormVisible, setEditFormVisible] = useState(false)
  const [addFormVisible, setAddFormVisible] = useState(false)
  const [toolId, setToolId] = useState(0)
  const { showToast } = useToast()

  const [dataState, setDataState] = useState({
    skip: 0,
    take: 25,
    filter: undefined,
    sort: undefined,
  })

  const handleAddForm = () => {
    setAddFormVisible(true)
    setEditFormVisible(false)
    handleExpanded()
  }

  const handleEditForm = (id: number) => {
    setToolId(id)
    setEditFormVisible(true)
    setAddFormVisible(false)
    handleExpanded()
  }

  const updateToolHandler = (dataItem: UpdateTool, id: number) => {
    updateTool(dataItem, id)
      .then(() => {
        const fetchData = async () => {
          const data = await getTools()
          setToolData(data)
          setTotalRecords(data.length)
          handleExpanded()
          showToast('Tool successfully updated', 'success')
        }
        fetchData()
      })
      .catch((error) => {
        console.error('Error updating tool:', error)
        handleExpanded()
        showToast(`Error updating tool: ${error.message}`, 'error')
        setErrorMessage(error.message)
      })
  }

  const newToolHandler = async (dataItem: PostTool) => {
    try {
      await submitTool(dataItem)

      const data = await getTools()
      setToolData(data)
      setTotalRecords(data.length)

      handleExpanded()
      showToast('Tool successfully added', 'success')
      setErrorMessage(null)
    } catch (err) {
      handleExpanded()
      const axiosErr = err as AxiosError
      showToast('Failed to add tool. Please try again.', 'error')
      setErrorMessage(axiosErr.message)
    }
  }

  const loadTools = async () => {
    try {
      setLoading(true)
      const data = await getTools()
      setToolData(data)
      setTotalRecords(data.length)
    } catch (error) {
      console.error('Error loading tools:', error)
      showToast('Error loading tools', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTools()
  }, [])

  const deleteToolHandler = (id: number) => {
    deleteTool(id)
      .then(() => {
        setToolData((prevData) => prevData.filter((t) => t.toolId !== id))
        setTotalRecords((prev) => prev - 1)
        handleExpanded()
        showToast('Tool successfully deleted', 'success')
      })
      .catch((error) => {
        console.error('Error deleting tool:', error)
        handleExpanded()
        showToast('Error deleting tool', 'error')
      })
  }

  const handleDataStateChange = (event: any) => {
    setDataState(event.dataState)
  }

  const handleExpanded = () => {
    setExpanded((prevState) => !prevState)

    if (editFormVisible) {
      setEditFormVisible(false)
    }
    if (addFormVisible) {
      setAddFormVisible(false)
    }
  }

  const handleSelect = () => {
    setExpanded(false)
  }

  const processedData = useMemo(() => {
    return process(toolData, dataState)
  }, [toolData, dataState])

  return (
    <div>
      <Drawer
        expanded={expanded}
        position={'end'}
        width={750}
        mode='overlay'
        animation={{ duration: 400 }}
        onOverlayClick={handleExpanded}
        onSelect={handleSelect}
      >
        <DrawerContent>
          <DrawerNavigation>
            {addFormVisible && canEdit && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>Add Tool</h1>
                  <FaTimesCircle className='closeform' onClick={handleExpanded} />
                </div>
                <div className='form__wrapper'>
                  <AddToolForm
                    onToolSubmit={newToolHandler}
                    onErrorMessage={errorMessage}
                  />
                </div>
              </>
            )}

            {editFormVisible && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>{canEdit ? 'Edit Tool' : 'Tool'}</h1>
                  <FaTimesCircle className='closeform' onClick={handleExpanded} />
                </div>
                <div className='form__wrapper'>
                  <EditToolForm
                    key={toolId}
                    onEditSubmit={updateToolHandler}
                    onToolId={toolId}
                    onToolDelete={deleteToolHandler}
                    canEdit={canEdit}
                  />
                </div>
              </>
            )}
          </DrawerNavigation>
        </DrawerContent>
      </Drawer>

      <ToolsGrid
        data={processedData}
        loading={loading}
        dataState={dataState}
        onDataStateChange={handleDataStateChange}
        onAddToggle={handleAddForm}
        onEditToggle={handleEditForm}
        canEdit={canEdit}
      />
    </div>
  )
}

export default Tools
